import React from 'react';
import { MdClose, MdWarning, MdSearch } from '../../utils/icons';

// Status Badge
export function Badge({ statusConfig, text }) {
  return (
    <span className={`badge ${statusConfig?.color || 'bg-slate-100 text-slate-700'}`}>
      {statusConfig?.dot && <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`}></span>}
      {text || statusConfig?.label}
    </span>
  );
}

// Modal
export function Modal({ isOpen, onClose, title, children, size = 'md' }) {
  if (!isOpen) return null;
  
  const sizes = { 
    sm: 'max-w-md', 
    md: 'max-w-2xl', 
    lg: 'max-w-4xl', 
    xl: 'max-w-6xl' 
  };
  
  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"  // z-index خیلی بالا
      onClick={onClose}
      style={{ margin: 0, top: 0, left: 0, right: 0, bottom: 0 }}  // حتماً تمام صفحه رو بگیره
    >
      {/* بک‌دراپ با z-index پایین‌تر */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm" 
        style={{ zIndex: 1 }}
      />
      
      {/* مودال اصلی با z-index بالاتر از بک‌دراپ */}
      <div
        className={`relative glass-card w-full ${sizes[size]} flex flex-col fade-in`}
        style={{ 
          zIndex: 2,
          maxHeight: '85vh',
          margin: 'auto'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* هدر - ثابت */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-800">{title}</h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg p-1.5 transition-colors"
          >
            <MdClose size={20} />
          </button>
        </div>
        
        {/* محتوای قابل اسکرول */}
        <div className="flex-1 overflow-y-auto p-5">
          {children}
        </div>
      </div>
    </div>
  );
}

// Confirm Dialog
export function ConfirmDialog({ isOpen, onClose, onConfirm, title, message }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative glass-card w-full max-w-sm p-6 fade-in text-center">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
          <MdWarning className="text-red-600" size={32} />
        </div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">{title || 'آیا مطمئن هستید؟'}</h3>
        <p className="text-gray-500 text-sm mb-6">{message}</p>
        <div className="flex gap-3 justify-center">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors text-sm font-medium">انصراف</button>
          <button onClick={onConfirm} className="px-5 py-2.5 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-colors text-sm font-medium">بله، حذف شود</button>
        </div>
      </div>
    </div>
  );
}

// Loading Spinner
export function Spinner({ size = 'md' }) {
  const s = { sm: 'w-5 h-5', md: 'w-8 h-8', lg: 'w-12 h-12' };
  return (
    <div className={`${s[size]} border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin`} />
  );
}

// Page Loader
export function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-96">
      <div className="text-center">
        <Spinner size="lg" />
        <p className="text-gray-500 text-sm mt-4">در حال بارگذاری...</p>
      </div>
    </div>
  );
}

// Empty State
export function EmptyState({ icon, title, subtitle, action }) {
  return (
    <div className="text-center py-16">
      <div className="text-6xl mb-4">{icon || '📭'}</div>
      <h3 className="text-gray-800 font-semibold text-lg mb-2">{title || 'داده‌ای یافت نشد'}</h3>
      {subtitle && <p className="text-gray-500 text-sm mb-6">{subtitle}</p>}
      {action}
    </div>
  );
}

// Form Field
export function FormField({ label, required, error, children }) {
  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-600 mb-1.5">
          {label} {required && <span className="text-red-600">*</span>}
        </label>
      )}
      {children}
      {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
    </div>
  );
}

// Search Input
export function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <MdSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'جستجو...'}
        className="form-input pr-9"
      />
    </div>
  );
}

// Stat Card — smaller & cleaner
export function StatCard({ icon, label, value, sub, color = 'blue', trend, onClick }) {
  const colors = {
    blue: 'from-blue-600/15 to-blue-800/5 border-blue-200',
    green: 'from-green-600/15 to-green-800/5 border-green-200',
    red: 'from-red-600/15 to-red-800/5 border-red-200',
    purple: 'from-purple-600/15 to-purple-800/5 border-purple-200',
    orange: 'from-orange-600/15 to-orange-800/5 border-orange-200',
    yellow: 'from-yellow-600/15 to-yellow-800/5 border-yellow-200',
  };
  const iconColors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    yellow: 'bg-yellow-100 text-yellow-600',
  };
  return (
    <div
      onClick={onClick}
      className={`glass-card bg-gradient-to-br ${colors[color]} p-4 ${onClick ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md transition-all' : ''}`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-base flex-shrink-0 ${iconColors[color]}`}>
          {icon}
        </div>
        {trend !== undefined && (
          <span className={`text-xs font-medium px-1.5 py-0.5 rounded-md mr-auto ${trend >= 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className="text-xl font-bold text-gray-800 leading-tight">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
      {sub && <div className="text-xs text-gray-600 mt-0.5">{sub}</div>}
    </div>
  );
}

// Progress Bar
export function ProgressBar({ value, max = 100, label, color = 'blue' }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const colors = { blue: 'from-blue-500 to-blue-400', green: 'from-green-500 to-green-400', red: 'from-red-500 to-red-400', yellow: 'from-yellow-500 to-yellow-400' };
  return (
    <div>
      {label && <div className="flex justify-between text-xs text-gray-500 mb-1"><span>{label}</span><span>{Math.round(pct)}%</span></div>}
      <div className="progress-bar">
        <div className={`progress-bar-fill bg-gradient-to-r ${colors[color]}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// Action Button
export function ActionBtn({ onClick, icon, label, variant = 'primary', size = 'md', disabled = false }) {
  const variants = {
    primary: 'bg-blue-600 hover:bg-blue-500 text-white',
    danger: 'bg-red-600/80 hover:bg-red-500 text-white',
    ghost: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
    success: 'bg-green-600/80 hover:bg-green-500 text-white',
    warning: 'bg-yellow-600/80 hover:bg-yellow-500 text-white',
  };
  const sizes = { sm: 'px-3 py-1.5 text-xs gap-1.5', md: 'px-4 py-2 text-sm gap-2', lg: 'px-6 py-3 text-base gap-2' };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center font-medium rounded-xl transition-all ${variants[variant]} ${sizes[size]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {icon && <span>{icon}</span>}
      {label}
    </button>
  );
}

// Section Header
export function SectionHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <div>
        <h1 className="text-xl font-bold text-gray-800">{title}</h1>
        {subtitle && <p className="text-gray-500 text-sm mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

// Pagination
export function Pagination({ page, totalPages, onChange }) {
  if (!totalPages || totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-gray-100">
      <button
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        قبلی
      </button>
      <span className="px-3 py-1.5 text-sm text-gray-600">صفحه {page} از {totalPages}</span>
      <button
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        بعدی
      </button>
    </div>
  );
}

// کمک‌کننده: برش یک آرایه برای نمایش صفحه‌بندی‌شده در سمت کلاینت
export function paginate(items, page, pageSize) {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}
